import { adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  vehicleId: string;
}

/**
 * 28-day rolling availability view. Each day cell shows one of:
 *   ● free   — nothing booked, no block
 *   ● booked — overlapped by a `bookings` row (state-aware colour)
 *   ● block  — covered by an `availability_blocks` row
 *
 * Clicking a free day starts a "create block" inline form covering that single day (the
 * admin can extend the range in the modal); clicking an existing block opens delete.
 */
export function AvailabilityCalendar({ vehicleId }: Props) {
  const qc = useQueryClient();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const from = new Date(today);
  const to = new Date(today);
  to.setDate(to.getDate() + 28);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'vehicle', vehicleId, 'availability', from.toISOString(), to.toISOString()],
    queryFn: () => adminApi.vehicleAvailability(vehicleId, from.toISOString(), to.toISOString()),
  });

  const [blockingDay, setBlockingDay] = useState<Date | null>(null);
  // Block awaiting removal confirmation (inline strip replaces window.confirm).
  const [unblockRef, setUnblockRef] = useState<string | null>(null);
  const [blockEnd, setBlockEnd] = useState<string>('');
  const [blockReason, setBlockReason] = useState<'maintenance' | 'manual_block'>('maintenance');

  const createBlock = useMutation({
    mutationFn: () => {
      if (!blockingDay) throw new Error('no_day');
      const start = new Date(blockingDay);
      const end = blockEnd ? new Date(blockEnd) : new Date(start);
      end.setDate(end.getDate() + 1);
      return adminApi.createBlock(vehicleId, {
        fromAt: start.toISOString(),
        toAt: end.toISOString(),
        reason: blockReason,
      });
    },
    onSuccess: () => {
      setBlockingDay(null);
      setBlockEnd('');
      qc.invalidateQueries({ queryKey: ['admin', 'vehicle', vehicleId, 'availability'] });
    },
  });

  const deleteBlock = useMutation({
    mutationFn: (id: string) => adminApi.deleteBlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'vehicle', vehicleId, 'availability'] }),
  });

  const days: Date[] = [];
  for (let i = 0; i < 28; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  function classify(d: Date): { kind: 'free' | 'booked' | 'block'; ref?: string; state?: string } {
    if (!data) return { kind: 'free' };
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    const booking = data.bookings.find((b) => new Date(b.fromAt) < next && new Date(b.toAt) > d);
    if (booking) return { kind: 'booked', ref: booking.code, state: booking.state };
    const block = data.blocks.find((b) => new Date(b.fromAt) < next && new Date(b.toAt) > d);
    if (block) return { kind: 'block', ref: block.id, state: block.reason };
    return { kind: 'free' };
  }

  const wd = new Intl.DateTimeFormat('pt-PT', { weekday: 'short' });

  return (
    <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
      <div className="label" style={{ color: 'var(--lv-brand)' }}>
        CALENDÁRIO · 28 DIAS
      </div>
      {isLoading && <div style={{ marginTop: 12, color: 'var(--lv-gray-500)' }}>A carregar…</div>}
      {unblockRef && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--lv-err)' }}>
            Remover este bloqueio?
          </span>
          <button
            type="button"
            onClick={() => {
              deleteBlock.mutate(unblockRef);
              setUnblockRef(null);
            }}
            style={{ ...btnPrimary, background: 'var(--lv-err)' }}
          >
            Confirmar
          </button>
          <button type="button" onClick={() => setUnblockRef(null)} style={btnGhost}>
            Cancelar
          </button>
        </div>
      )}
      {data && (
        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
          }}
        >
          {days.map((d) => {
            const c = classify(d);
            const isPickable = c.kind === 'free';
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => {
                  if (c.kind === 'block' && c.ref) {
                    setUnblockRef(c.ref);
                  } else if (isPickable) {
                    setBlockingDay(d);
                    setBlockEnd(d.toISOString().slice(0, 10));
                  }
                }}
                style={{
                  position: 'relative',
                  padding: '10px 6px',
                  border: '1px solid var(--lv-line)',
                  background: cellBg(c.kind, c.state),
                  color: cellFg(c.kind),
                  textAlign: 'left',
                  cursor: isPickable || c.kind === 'block' ? 'pointer' : 'default',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {wd.format(d)}
                </span>
                <br />
                <span style={{ fontFamily: 'var(--lv-font-mono)', fontSize: 14, fontWeight: 700 }}>
                  {d.getDate().toString().padStart(2, '0')}
                </span>
                {c.kind !== 'free' && (
                  <span style={{ display: 'block', fontSize: 9, marginTop: 4, opacity: 0.8 }}>{c.ref}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 12, fontSize: 11, color: 'var(--lv-gray-500)' }}>
        <Legend swatch="#eaf0ee" label="livre" />
        <Legend swatch="#fee4e2" label="reservada" />
        <Legend swatch="#fef3c7" label="bloqueio" />
      </div>

      {blockingDay && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12, 35, 34, 0.55)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 90,
          }}
          onClick={() => setBlockingDay(null)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              createBlock.mutate();
            }}
            style={{ background: '#fff', width: 360, padding: 24, border: '1px solid var(--lv-line)' }}
          >
            <div className="display" style={{ fontSize: 20 }}>
              BLOQUEAR DIAS
            </div>
            <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
              <label>
                <span
                  style={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  Início
                </span>
                <span
                  style={{
                    marginTop: 4,
                    display: 'block',
                    padding: '8px 10px',
                    background: 'var(--lv-surface-soft)',
                    border: '1px solid var(--lv-line)',
                    fontSize: 12,
                  }}
                >
                  {blockingDay.toLocaleDateString('pt-PT')}
                </span>
              </label>
              <label>
                <span
                  style={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  Último dia bloqueado
                </span>
                <input
                  type="date"
                  value={blockEnd}
                  onChange={(e) => setBlockEnd(e.target.value)}
                  min={blockingDay.toISOString().slice(0, 10)}
                  style={{
                    marginTop: 4,
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--lv-line)',
                    background: '#fff',
                    fontSize: 13,
                  }}
                  required
                />
              </label>
              <label>
                <span
                  style={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  Motivo
                </span>
                <select
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value as typeof blockReason)}
                  style={{
                    marginTop: 4,
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--lv-line)',
                    background: '#fff',
                    fontSize: 13,
                  }}
                >
                  <option value="maintenance">Manutenção</option>
                  <option value="manual_block">Bloqueio manual</option>
                </select>
              </label>
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setBlockingDay(null)} style={btnGhost}>
                Cancelar
              </button>
              <button type="submit" disabled={createBlock.isPending} style={btnPrimary}>
                {createBlock.isPending ? '…' : 'Bloquear'}
              </button>
            </div>
            {createBlock.error && (
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#fce4e4',
                  color: 'var(--lv-err)',
                  fontSize: 12,
                }}
              >
                {(createBlock.error as Error).message}
              </div>
            )}
          </form>
        </div>
      )}
    </section>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, background: swatch, border: '1px solid var(--lv-line)' }} />
      {label}
    </span>
  );
}

function cellBg(kind: 'free' | 'booked' | 'block', state?: string): string {
  if (kind === 'booked') {
    if (state === 'in_progress') return '#fecaca';
    if (state === 'confirmed') return '#fee4e2';
    return '#fff1f0';
  }
  if (kind === 'block') return '#fef3c7';
  return '#eaf0ee';
}
function cellFg(kind: 'free' | 'booked' | 'block'): string {
  if (kind === 'booked') return '#7c2d12';
  if (kind === 'block') return '#78350f';
  return 'var(--lv-brand)';
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
